import { useState } from 'react';
import Shell from './components/Shell';
import Login from './pages/Login';
import AffiliateDashboard from './pages/AffiliateDashboard';
import ParentAffiliateDashboard from './pages/ParentAffiliateDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AffiliateManagement from './pages/AffiliateManagement';
import ChannelManagement from './pages/ChannelManagement';
import PromoManagement from './pages/PromoManagement';
import DeviceList from './pages/DeviceList';
import OTAManagement from './pages/OTAManagement';
import LogViewer from './pages/LogViewer';
import ActivityLog from './pages/ActivityLog';
import UsersManagement from './pages/UsersManagement';
import ProfileSettings from './pages/ProfileSettings';
import ResetPassword from './pages/ResetPassword';
import { useTheme } from './context/ThemeContext';
import { api, auth } from './data/api';

export default function App() {
  // Hydrate session from localStorage so refresh doesn't kick the user to /login
  // while their JWT is still valid. requireAuth on the server is still the gate.
  const defaultActiveFor = (u) => {
    if (!u) return "affiliates";
    if (u.role === "promo") return "dashboard";
    if (u.role === "custom") {
      const granted = Object.entries(u.permissions || {}).find(([, lvl]) => lvl !== "none");
      return granted ? granted[0] : "dashboard";
    }
    return "affiliates";
  };

  const [user, setUser]     = useState(auth.user);
  const [active, setActive] = useState(defaultActiveFor(auth.user));
  const { mode } = useTheme();

  const [isParentAffiliate, setIsParentAffiliate] = useState(null); // null=loading, true/false
  const [promoChannel, setPromoChannel]   = useState(null);
  const [promoViewOnly, setPromoViewOnly] = useState(false);
  const [promoCounts, setPromoCounts] = useState({});
  const [searchSeed, setSearchSeed] = useState("");

  const navigateFromSearch = (type, id) => {
    if (type === "affiliate") setActive("affiliates");
    if (type === "channel")   setActive("channels");
    if (type === "device") {
      setSearchSeed(id);
      setActive("devices");
    }
  };

  const openPromoPage = (channel, viewOnly = false) => {
    setPromoChannel(channel);
    setPromoViewOnly(viewOnly);
    setActive("promos");
  };

  const closePromoPage = () => {
    setActive("channels");
    setPromoChannel(null);
  };

  const updatePromoCount = (channelId, count) => {
    setPromoCounts(prev => ({ ...prev, [channelId]: count }));
  };

  // ?reset=<token> from an account-created or forgot-password email. Checked
  // before the login gate — most people clicking this link are logged out,
  // but it also works if someone's already logged in elsewhere.
  const resetToken = new URLSearchParams(window.location.search).get('reset');
  if (resetToken) {
    return (
      <ResetPassword
        token={resetToken}
        onDone={() => {
          window.history.replaceState({}, '', window.location.pathname);
          window.location.reload();
        }}
      />
    );
  }

  if (!user) return <Login onLogin={u => { setUser(u); setActive(defaultActiveFor(u)); }} />;

  if (user.role === "affiliate") {
    // Check if this affiliate has children (is a master/parent affiliate)
    // We store the result in state to avoid flicker
    if (isParentAffiliate === null) {
      api.affiliates.parentDashboard(user.affiliate_id)
        .then(res => setIsParentAffiliate((res.children || []).length > 0))
        .catch(() => setIsParentAffiliate(false));
      return (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontSize:12, color:'var(--text-dim)' }}>
          Loading…
        </div>
      );
    }
    return (
      <Shell active="my-devices" setActive={() => {}} user={user} onLogout={() => { api.auth.logout(); setUser(null); }}>
        {isParentAffiliate
          ? <ParentAffiliateDashboard user={user} />
          : <AffiliateDashboard user={user} />
        }
      </Shell>
    );
  }

  // ── User role — dashboard, affiliates, devices, activity log ────────────
  if (user.role === "user") return (
    <Shell active={active} setActive={setActive} user={user} onLogout={() => { api.auth.logout(); setUser(null); }}>
      {active === "dashboard"  && <AdminDashboard onNavigate={navigateFromSearch} searchScope={["affiliate", "device"]} />}
      {active === "affiliates" && <AffiliateManagement />}
      {active === "devices"    && <DeviceList initialSearch={searchSeed} />}
      {active === "profile" && <ProfileSettings user={user} onUpdated={(u) => setUser(u)} />}
      {active === "activity"   && <ActivityLog />}
    </Shell>
  );

  // ── Promo role — dashboard + channels (view only), can upload/delete promos
  if (user.role === "promo") return (
    <Shell active={active} setActive={setActive} user={user} onLogout={() => { api.auth.logout(); setUser(null); }}>
      {active === "dashboard" && <AdminDashboard />}
      {active === "channels"  && (
        <ChannelManagement
          readOnly={true}
          onOpenPromo={openPromoPage}
          promoCounts={promoCounts}
          setPromoCounts={setPromoCounts}
        />
      )}
      {active === "profile" && <ProfileSettings user={user} onUpdated={(u) => setUser(u)} />}
      {active === "promos" && (
        <PromoManagement
          channel={promoChannel}
          viewOnly={false}
          onBack={closePromoPage}
          onCountChange={updatePromoCount}
        />
      )}
    </Shell>
  );

  // ── Super User role — everything admin has, plus User Management ───────
  if (user.role === "superuser") return (
    <Shell active={active} setActive={setActive} user={user} onLogout={() => { api.auth.logout(); setUser(null); }}>
      {active === "dashboard"  && <AdminDashboard onNavigate={navigateFromSearch} searchScope={["affiliate", "device", "channel"]} />}
      {active === "affiliates" && <AffiliateManagement />}
      {active === "channels"   && (
        <ChannelManagement
          onOpenPromo={openPromoPage}
          promoCounts={promoCounts}
          setPromoCounts={setPromoCounts}
        />
      )}
      {active === "promos"     && (
        <PromoManagement
          channel={promoChannel}
          viewOnly={promoViewOnly}
          onBack={closePromoPage}
          onCountChange={updatePromoCount}
        />
      )}
      {active === "devices"    && <DeviceList initialSearch={searchSeed} />}
      {active === "ota"        && <OTAManagement />}
      {active === "logs"       && <LogViewer />}
      {active === "activity"   && <ActivityLog />}
      {active === "profile" && <ProfileSettings user={user} onUpdated={(u) => setUser(u)} />}
      {active === "users"      && <UsersManagement />}
    </Shell>
  );

  if (user.role === "custom") {
    const perm = (key) => user.permissions?.[key] || "none";
    const has  = (key) => perm(key) !== "none";
    const ro   = (key) => perm(key) === "view";

    return (
      <Shell active={active} setActive={setActive} user={user} onLogout={() => { api.auth.logout(); setUser(null); }}>
        {active === "dashboard" && has("dashboard") && (
          <AdminDashboard
            onNavigate={navigateFromSearch}
            searchScope={["affiliate", "device", "channel"].filter(t =>
              has(t === "affiliate" ? "affiliates" : t === "device" ? "devices" : "channels")
            )}
          />
        )}
        {active === "affiliates" && has("affiliates") && <AffiliateManagement readOnly={ro("affiliates")} />}
        {active === "channels"   && has("channels") && (
          <ChannelManagement
            readOnly={ro("channels")}
            onOpenPromo={openPromoPage}
            promoCounts={promoCounts}
            setPromoCounts={setPromoCounts}
          />
        )}
        {active === "promos" && has("channels") && (
          <PromoManagement
            channel={promoChannel}
            viewOnly={ro("channels")}
            onBack={closePromoPage}
            onCountChange={updatePromoCount}
          />
        )}
        {active === "devices" && has("devices") && <DeviceList initialSearch={searchSeed} readOnly={ro("devices")} />}
        {active === "ota"     && has("ota")     && <OTAManagement readOnly={ro("ota")} canDelete={false} />}
        {active === "logs"    && has("logs")    && <LogViewer />}
        {active === "profile" && <ProfileSettings user={user} onUpdated={(u) => setUser(u)} />}
        {active === "activity" && has("activity") && <ActivityLog />}
      </Shell>
    );
  }

  // ── Admin role — full access ────────────────────────────────────────────
  return (
    <Shell active={active} setActive={setActive} user={user} onLogout={() => { api.auth.logout(); setUser(null); }}>
      {active === "dashboard"  && <AdminDashboard onNavigate={navigateFromSearch} searchScope={["affiliate", "device", "channel"]} />}
      {active === "affiliates" && <AffiliateManagement />}
      {active === "channels"   && (
        <ChannelManagement
          onOpenPromo={openPromoPage}
          promoCounts={promoCounts}
          setPromoCounts={setPromoCounts}
        />
      )}
      {active === "promos"     && (
        <PromoManagement
          channel={promoChannel}
          viewOnly={promoViewOnly}
          onBack={closePromoPage}
          onCountChange={updatePromoCount}
        />
      )}
      {active === "devices"    && <DeviceList initialSearch={searchSeed} />}
      {active === "ota"        && <OTAManagement />}
      {active === "logs"       && <LogViewer />}
      {active === "activity"   && <ActivityLog />}
      {active === "profile"   && <ProfileSettings user={user} onUpdated={(u) => setUser(u)} />}
    </Shell>
  );
}

//platformauth added
// import { useState } from 'react';
// import Shell from './components/Shell';
// import Login from './pages/Login';
// import PlatformAuth from './pages/PlatformAuth';
// import AffiliateDashboard from './pages/AffiliateDashboard';
// import ParentAffiliateDashboard from './pages/ParentAffiliateDashboard';
// import AdminDashboard from './pages/AdminDashboard';
// import AffiliateManagement from './pages/AffiliateManagement';
// import ChannelManagement from './pages/ChannelManagement';
// import PromoManagement from './pages/PromoManagement';
// import DeviceList from './pages/DeviceList';
// import OTAManagement from './pages/OTAManagement';
// import LogViewer from './pages/LogViewer';
// import ActivityLog from './pages/ActivityLog';
// import UsersManagement from './pages/UsersManagement';
// import ProfileSettings from './pages/ProfileSettings';
// import ResetPassword from './pages/ResetPassword';
// import { useTheme } from './context/ThemeContext';
// import { api, auth } from './data/api';

// export default function App() {
//   // Hydrate session from localStorage so refresh doesn't kick the user to /login
//   // while their JWT is still valid. requireAuth on the server is still the gate.
//   const defaultActiveFor = (u) => {
//     if (!u) return "affiliates";
//     if (u.role === "promo") return "dashboard";
//     if (u.role === "custom") {
//       const granted = Object.entries(u.permissions || {}).find(([, lvl]) => lvl !== "none");
//       return granted ? granted[0] : "dashboard";
//     }
//     return "affiliates";
//   };

//   const [user, setUser]     = useState(auth.user);
//   const [active, setActive] = useState(defaultActiveFor(auth.user));
//   const { mode } = useTheme();

//   const [isParentAffiliate, setIsParentAffiliate] = useState(null); // null=loading, true/false
//   const [promoChannel, setPromoChannel]   = useState(null);
//   const [promoViewOnly, setPromoViewOnly] = useState(false);
//   const [promoCounts, setPromoCounts] = useState({});
//   const [searchSeed, setSearchSeed] = useState("");

//   const navigateFromSearch = (type, id) => {
//     if (type === "affiliate") setActive("affiliates");
//     if (type === "channel")   setActive("channels");
//     if (type === "device") {
//       setSearchSeed(id);
//       setActive("devices");
//     }
//   };

//   const openPromoPage = (channel, viewOnly = false) => {
//     setPromoChannel(channel);
//     setPromoViewOnly(viewOnly);
//     setActive("promos");
//   };

//   const closePromoPage = () => {
//     setActive("channels");
//     setPromoChannel(null);
//   };

//   const updatePromoCount = (channelId, count) => {
//     setPromoCounts(prev => ({ ...prev, [channelId]: count }));
//   };

//   // DR Pulse 360 → SRT Manager handoff: /auth?token=<scoped_jwt>
//   // Checked before the login gate so the platform redirect lands directly.
//   if (window.location.pathname === '/auth') {
//     const platformToken = new URLSearchParams(window.location.search).get('token');
//     if (platformToken) {
//       return (
//         <PlatformAuth
//           onLogin={u => {
//             setUser(u);
//             setActive(defaultActiveFor(u));
//             window.history.replaceState({}, '', '/');
//           }}
//         />
//       );
//     }
//   }

//   // ?reset=<token> from an account-created or forgot-password email. Checked
//   // before the login gate — most people clicking this link are logged out,
//   // but it also works if someone's already logged in elsewhere.
//   const resetToken = new URLSearchParams(window.location.search).get('reset');
//   if (resetToken) {
//     return (
//       <ResetPassword
//         token={resetToken}
//         onDone={() => {
//           window.history.replaceState({}, '', window.location.pathname);
//           window.location.reload();
//         }}
//       />
//     );
//   }

//   if (!user) return <Login onLogin={u => { setUser(u); setActive(defaultActiveFor(u)); }} />;

//   if (user.role === "affiliate") {
//     // Check if this affiliate has children (is a master/parent affiliate)
//     // We store the result in state to avoid flicker
//     if (isParentAffiliate === null) {
//       api.affiliates.parentDashboard(user.affiliate_id)
//         .then(res => setIsParentAffiliate((res.children || []).length > 0))
//         .catch(() => setIsParentAffiliate(false));
//       return (
//         <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontSize:12, color:'var(--text-dim)' }}>
//           Loading…
//         </div>
//       );
//     }
//     return (
//       <Shell active="my-devices" setActive={() => {}} user={user} onLogout={() => { api.auth.logout(); setUser(null); }}>
//         {isParentAffiliate
//           ? <ParentAffiliateDashboard user={user} />
//           : <AffiliateDashboard user={user} />
//         }
//       </Shell>
//     );
//   }

//   // ── User role — dashboard, affiliates, devices, activity log ────────────
//   if (user.role === "user") return (
//     <Shell active={active} setActive={setActive} user={user} onLogout={() => { api.auth.logout(); setUser(null); }}>
//       {active === "dashboard"  && <AdminDashboard onNavigate={navigateFromSearch} searchScope={["affiliate", "device"]} />}
//       {active === "affiliates" && <AffiliateManagement />}
//       {active === "devices"    && <DeviceList initialSearch={searchSeed} />}
//       {active === "profile" && <ProfileSettings user={user} onUpdated={(u) => setUser(u)} />}
//       {active === "activity"   && <ActivityLog />}
//     </Shell>
//   );

//   // ── Promo role — dashboard + channels (view only), can upload/delete promos
//   if (user.role === "promo") return (
//     <Shell active={active} setActive={setActive} user={user} onLogout={() => { api.auth.logout(); setUser(null); }}>
//       {active === "dashboard" && <AdminDashboard />}
//       {active === "channels"  && (
//         <ChannelManagement
//           readOnly={true}
//           onOpenPromo={openPromoPage}
//           promoCounts={promoCounts}
//           setPromoCounts={setPromoCounts}
//         />
//       )}
//       {active === "profile" && <ProfileSettings user={user} onUpdated={(u) => setUser(u)} />}
//       {active === "promos" && (
//         <PromoManagement
//           channel={promoChannel}
//           viewOnly={false}
//           onBack={closePromoPage}
//           onCountChange={updatePromoCount}
//         />
//       )}
//     </Shell>
//   );

//   // ── Super User role — everything admin has, plus User Management ───────
//   if (user.role === "superuser") return (
//     <Shell active={active} setActive={setActive} user={user} onLogout={() => { api.auth.logout(); setUser(null); }}>
//       {active === "dashboard"  && <AdminDashboard onNavigate={navigateFromSearch} searchScope={["affiliate", "device", "channel"]} />}
//       {active === "affiliates" && <AffiliateManagement />}
//       {active === "channels"   && (
//         <ChannelManagement
//           onOpenPromo={openPromoPage}
//           promoCounts={promoCounts}
//           setPromoCounts={setPromoCounts}
//         />
//       )}
//       {active === "promos"     && (
//         <PromoManagement
//           channel={promoChannel}
//           viewOnly={promoViewOnly}
//           onBack={closePromoPage}
//           onCountChange={updatePromoCount}
//         />
//       )}
//       {active === "devices"    && <DeviceList initialSearch={searchSeed} />}
//       {active === "ota"        && <OTAManagement />}
//       {active === "logs"       && <LogViewer />}
//       {active === "activity"   && <ActivityLog />}
//       {active === "profile" && <ProfileSettings user={user} onUpdated={(u) => setUser(u)} />}
//       {active === "users"      && <UsersManagement />}
//     </Shell>
//   );

//   if (user.role === "custom") {
//     const perm = (key) => user.permissions?.[key] || "none";
//     const has  = (key) => perm(key) !== "none";
//     const ro   = (key) => perm(key) === "view";

//     return (
//       <Shell active={active} setActive={setActive} user={user} onLogout={() => { api.auth.logout(); setUser(null); }}>
//         {active === "dashboard" && has("dashboard") && (
//           <AdminDashboard
//             onNavigate={navigateFromSearch}
//             searchScope={["affiliate", "device", "channel"].filter(t =>
//               has(t === "affiliate" ? "affiliates" : t === "device" ? "devices" : "channels")
//             )}
//           />
//         )}
//         {active === "affiliates" && has("affiliates") && <AffiliateManagement readOnly={ro("affiliates")} />}
//         {active === "channels"   && has("channels") && (
//           <ChannelManagement
//             readOnly={ro("channels")}
//             onOpenPromo={openPromoPage}
//             promoCounts={promoCounts}
//             setPromoCounts={setPromoCounts}
//           />
//         )}
//         {active === "promos" && has("channels") && (
//           <PromoManagement
//             channel={promoChannel}
//             viewOnly={ro("channels")}
//             onBack={closePromoPage}
//             onCountChange={updatePromoCount}
//           />
//         )}
//         {active === "devices" && has("devices") && <DeviceList initialSearch={searchSeed} readOnly={ro("devices")} />}
//         {active === "ota"     && has("ota")     && <OTAManagement readOnly={ro("ota")} canDelete={false} />}
//         {active === "logs"    && has("logs")    && <LogViewer />}
//         {active === "profile" && <ProfileSettings user={user} onUpdated={(u) => setUser(u)} />}
//         {active === "activity" && has("activity") && <ActivityLog />}
//       </Shell>
//     );
//   }

//   // ── Admin role — full access ────────────────────────────────────────────
//   return (
//     <Shell active={active} setActive={setActive} user={user} onLogout={() => { api.auth.logout(); setUser(null); }}>
//       {active === "dashboard"  && <AdminDashboard onNavigate={navigateFromSearch} searchScope={["affiliate", "device", "channel"]} />}
//       {active === "affiliates" && <AffiliateManagement />}
//       {active === "channels"   && (
//         <ChannelManagement
//           onOpenPromo={openPromoPage}
//           promoCounts={promoCounts}
//           setPromoCounts={setPromoCounts}
//         />
//       )}
//       {active === "promos"     && (
//         <PromoManagement
//           channel={promoChannel}
//           viewOnly={promoViewOnly}
//           onBack={closePromoPage}
//           onCountChange={updatePromoCount}
//         />
//       )}
//       {active === "devices"    && <DeviceList initialSearch={searchSeed} />}
//       {active === "ota"        && <OTAManagement />}
//       {active === "logs"       && <LogViewer />}
//       {active === "activity"   && <ActivityLog />}
//       {active === "profile"   && <ProfileSettings user={user} onUpdated={(u) => setUser(u)} />}
//     </Shell>
//   );
// }
